<script lang="ts">
  /**
   * LetterSelectScreen - Letter/Combination selection for Letter Mode
   * Allows choosing single letters (A-Z) or combinations (CH, SH, TH)
   */

  import StarField from '../ui/StarField.svelte';

  // Props
  interface Props {
    onSelectLetter?: (letter: string) => void;
    onBack?: () => void;
  }

  let { onSelectLetter = () => {}, onBack = () => {} }: Props = $props();

  // Single letters A-Z
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  // Combinations/Digraphs
  const combinations = ['CH', 'SH', 'TH'];

  // State
  let selectedCategory = $state<'letters' | 'combinations'>('letters');

  function handleSelect(item: string) {
    onSelectLetter(item);
  }
</script>

<div class="letter-select-screen">
  <StarField />

  <div class="content">
    <!-- Title -->
    <h1 class="title">Choose a Letter</h1>

    <!-- Category Tabs -->
    <div class="tabs">
      <button
        class="tab"
        class:active={selectedCategory === 'letters'}
        onclick={() => selectedCategory = 'letters'}
      >
        Letters (A-Z)
      </button>
      <button
        class="tab"
        class:active={selectedCategory === 'combinations'}
        onclick={() => selectedCategory = 'combinations'}
      >
        Combinations
      </button>
    </div>

    <!-- Letter Grid -->
    {#if selectedCategory === 'letters'}
      <div class="grid letters-grid">
        {#each letters as letter}
          <button
            class="letter-button"
            onclick={() => handleSelect(letter)}
          >
            {letter}
          </button>
        {/each}
      </div>
    {/if}

    <!-- Combinations Grid -->
    {#if selectedCategory === 'combinations'}
      <div class="grid combinations-grid">
        {#each combinations as combo}
          <button
            class="combination-button"
            onclick={() => handleSelect(combo)}
          >
            {combo}
          </button>
        {/each}
      </div>
    {/if}

    <!-- Back Button -->
    <button class="back-button" onclick={onBack}>
      ← Back
    </button>
  </div>
</div>

<style>
  .letter-select-screen {
    position: relative;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-xl);
    padding: var(--space-lg);
    width: 100%;
    max-width: 800px;
  }

  .title {
    font-size: var(--font-display);
    font-weight: bold;
    text-align: center;
    background: linear-gradient(
      135deg,
      var(--color-cosmic-purple),
      var(--color-bright-yellow)
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0;
  }

  /* Tabs */
  .tabs {
    display: flex;
    gap: var(--space-md);
    background: rgba(10, 22, 40, 0.7);
    padding: var(--space-sm);
    border-radius: 12px;
  }

  .tab {
    padding: var(--space-md) var(--space-lg);
    border: none;
    border-radius: 8px;
    background: transparent;
    color: var(--color-text-primary);
    font-size: var(--font-body);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .tab.active {
    background: var(--color-cosmic-purple);
    color: white;
  }

  .tab:hover:not(.active) {
    background: rgba(255, 255, 255, 0.1);
  }

  /* Grids */
  .grid {
    display: grid;
    gap: var(--space-md);
    width: 100%;
    max-height: 400px;
    overflow-y: auto;
    padding: var(--space-md);
    background: rgba(10, 22, 40, 0.5);
    border-radius: 16px;
  }

  .letters-grid {
    grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
  }

  .combinations-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    max-height: 200px;
  }

  /* Letter Buttons */
  .letter-button, .combination-button {
    aspect-ratio: 1;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%);
    color: white;
    font-size: 28px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .combination-button {
    font-size: 32px;
    padding: var(--space-lg);
  }

  .letter-button:hover, .combination-button:hover {
    transform: scale(1.1);
    box-shadow: 0 0 30px rgba(124, 58, 237, 0.6);
  }

  .letter-button:active, .combination-button:active {
    transform: scale(0.95);
  }

  /* Back Button */
  .back-button {
    padding: var(--space-md) var(--space-xl);
    border: none;
    border-radius: 12px;
    background: var(--color-space-blue);
    color: white;
    font-size: var(--font-body);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  .back-button:hover {
    transform: scale(1.05);
    background: var(--color-cosmic-purple);
  }

  .back-button:active {
    transform: scale(0.95);
  }

  /* Responsive */
  @media (max-width: 767px) {
    .title {
      font-size: 36px;
    }

    .letters-grid {
      grid-template-columns: repeat(auto-fill, minmax(50px, 1fr));
    }

    .letter-button, .combination-button {
      font-size: 24px;
    }

    .combinations-grid {
      grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    }
  }
</style>
